import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

const getStoredToken = () => localStorage.getItem('cloneia_access_token');
const getStoredRefresh = () => localStorage.getItem('cloneia_refresh_token');

const setStoredTokens = (access, refresh) => {
  if (access) localStorage.setItem('cloneia_access_token', access);
  if (refresh) localStorage.setItem('cloneia_refresh_token', refresh);
};

const clearStoredTokens = () => {
  localStorage.removeItem('cloneia_access_token');
  localStorage.removeItem('cloneia_refresh_token');
};

export const authFetch = async (url, opts = {}) => {
  const token = getStoredToken();
  const headers = { ...opts.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...opts, headers });
  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${getStoredToken()}`;
      return fetch(url, { ...opts, headers });
    }
  }
  return res;
};

const tryRefresh = async () => {
  const rt = getStoredRefresh();
  if (!rt) return false;
  try {
    const res = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data.access_token) {
      localStorage.setItem('cloneia_access_token', data.access_token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    fetch(`${API_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(async (res) => {
        if (res.ok) return res.json();
        const refreshed = await tryRefresh();
        if (refreshed) {
          const retry = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${getStoredToken()}` },
          });
          if (retry.ok) return retry.json();
        }
        throw new Error('Not authenticated');
      })
      .then(data => setUser(data))
      .catch(() => {
        clearStoredTokens();
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    } catch {
      throw new Error('Impossible de contacter le serveur');
    }
    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error('Erreur de connexion au serveur');
    }
    if (!res.ok) {
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Erreur de connexion');
    }
    setStoredTokens(data.access_token, data.refresh_token);
    const userData = { id: data.id, email: data.email, name: data.name, role: data.role };
    setUser(userData);
    return userData;
  };

  const logout = useCallback(async () => {
    const token = getStoredToken();
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
    } catch {}
    clearStoredTokens();
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
